import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { useThemeMode } from '../../ThemeContext';
import { dataAPI } from '../../helpers/api_service';
import { APP_URLS } from '../../helpers/url_helper';

// Import subscription CSS for billing toggle styles
import '../Subscription/Subscription.css';

// Import assets for PlanDetails section - using same as SubscriptionPlans
import GiftBox from '../../assets/gift-box 1.svg';
import Gift1 from '../../assets/gift 1.svg';
import Crown from '../../assets/Crown.svg';
import Rocket1 from '../../assets/rocket 1.svg';
import Checkmark from '../../assets/checkmark.svg';

function PlanDetails() {
  const { mode } = useThemeMode();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [billingToggle, setBillingToggle] = useState('MONTHLY');

  // Fetch subscription plans on component mount
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setLoading(true);
        const response = await dataAPI.getSubscriptionPlans(true); // include_trial = true for landing page
        if (response.status === 'success' && response.data) {
          setPlans(response.data);
        } else {
          setError('Failed to load subscription plans');
        }
      } catch (err) {
        console.error('Error fetching subscription plans:', err);
        setError('Failed to load subscription plans');
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, []);

  // Helper function to get plan icon - using same mapping as SubscriptionPlans
  const getPlanIcon = (planTitle) => {
    const title = planTitle.toLowerCase();
    if (title.includes('trial')) {
      return <img src={Gift1} className="feature-icon img-fluid" alt={`${planTitle} Plan`} />;
    } else if (title.includes('standard')) {
      return <img src={Gift1} className="feature-icon img-fluid" alt={`${planTitle} Plan`} />;
    } else if (title.includes('gold')) {
      return <img src={Crown} className="feature-icon img-fluid" alt={`${planTitle} Plan`} />;
    } else if (title.includes('platinum')) {
      return <img src={Rocket1} className="feature-icon img-fluid" alt={`${planTitle} Plan`} />;
    }
    // Default fallback
    return <img src={GiftBox} className="feature-icon img-fluid" alt={`${planTitle} Plan`} />;
  };

  // Helper function to format price display
  const formatPrice = (plan) => {
    // Show placeholder for enterprise plans to keep alignment
    if (plan && plan.is_enterprise) {
      return (
        <div className="mb-2">
          <span className="pricing-new-price">Custom pricing</span>
        </div>
      );
    }

    if (!plan.month_price) {
      return (
        <div className="mb-2">
          <span className="pricing-new-price">Free</span>
          <span className="pricing-monthly">/Trial</span>
        </div>
      );
    }
    
    if (billingToggle === 'YEARLY') {
      return (
        <div className="mb-2">
          <span className="pricing-new-price">${plan.year_price}</span>
          <span className="pricing-monthly">/Yearly</span>
        </div>
      );
    }
    
    return (
      <div className="mb-2">
        <span className="pricing-new-price">${plan.month_price}</span>
        <span className="pricing-monthly">/Monthly</span>
      </div>
    );
  };

  // Helper function to format description with line breaks
  const formatDescription = (formattedDescription) => {
    if (!formattedDescription) return [];
    
    return formattedDescription.split('\n').map((line, index) => {
      // Skip the first line as it's the title which we're using separately
      if (index === 0) return null;
      
      return (
        <li key={index}>
          <img src={Checkmark} className="pricing-check" alt="check" /> {line}
        </li>
      );
    }).filter(Boolean);
  };

  if (loading) {
    return (
      <section className="py-5" id="pricing">
        <Container className="text-center">
          <h2 className="section-title">Simple, Transparent Pricing</h2>
          <p className="section-subtitle mx-auto" style={{ maxWidth: '700px' }}>Loading subscription plans...</p>
        </Container>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-5" id="pricing">
        <Container className="text-center">
          <h2 className="section-title">Simple, Transparent Pricing</h2>
          <p className="section-subtitle mx-auto" style={{ maxWidth: '700px' }}>Error loading plans. Please try again later.</p>
        </Container>
      </section>
    );
  }

  return (
    <>
      {/* Pricing Section */}
      <section className="py-5" id="pricing">
        <Container fluid className="text-center" style={{ paddingLeft: '15px', paddingRight: '15px' }}>
          <h2 className="section-title">Simple, Transparent Pricing</h2>
          <p className="section-subtitle mx-auto" style={{ maxWidth: '700px' }}>Choose a plan that fits your team — no hidden charges, no surprises.</p>
          
          {/* Billing Toggle */}
          <div className="billing-toggle mb-4">
            <Button
              variant={billingToggle === 'MONTHLY' ? 'primary' : 'outline-secondary'}
              className={`toggle-btn ${billingToggle === 'MONTHLY' ? 'active' : ''}`}
              onClick={() => setBillingToggle('MONTHLY')}
            >
              MONTHLY
            </Button>
            <Button
              variant={billingToggle === 'YEARLY' ? 'primary' : 'outline-secondary'}
              className={`toggle-btn ${billingToggle === 'YEARLY' ? 'active' : ''}`}
              onClick={() => setBillingToggle('YEARLY')}
            >
              YEARLY
            </Button>
          </div>

          <Row className="mt-3 g-2 justify-content-center" style={{ margin: '0' }}>
            {plans.map((planData, index) => {
              const { plan, formatted_description } = planData;
              const isHighlighted = plan.title.toLowerCase() === 'gold'; // Highlight Gold plan
              
              return (
                <Col xs={12} md={3} key={plan.id} className="px-2">
                  <div className={`pricing-card h-100 d-flex flex-column ${isHighlighted ? 'border-teal border-2' : ''}`}>
                    <div className="flex-grow-1">
                      {getPlanIcon(plan.title)}
                      <h5 className="fw-bold mb-2">{plan.title} Plan</h5>
                      {formatPrice(plan)}
                      <ul className="list-unstyled text-start mx-auto pricing-features" style={{ maxWidth: '250px' }}>
                        {formatDescription(formatted_description)}
                      </ul>
                    </div>
                    <div className="mt-auto pt-3">
                      {plan.title.toLowerCase() === 'enterprise' ? (
                        <Button 
                          as={Link} 
                          to="/enquiry" 
                          state={{ 
                            planTitle: `${plan.title} Plan`,
                            billingCycle: billingToggle,
                            displayPrice: billingToggle === 'YEARLY' ? `$${plan.year_price}/Yearly` : (plan.month_price ? `$${plan.month_price}/Monthly` : 'Free/Trial'),
                            formattedDescription: formatted_description,
                            monthPrice: plan.month_price || null,
                            yearPrice: plan.year_price || null
                          }} 
                          variant="outline-primary" 
                          className="btn-outline-teal w-100"
                        >
                          Contact Sales
                        </Button>
                      ) : (
                        <Button as={Link} to={APP_URLS.SIGNUP} variant="outline-primary" className="btn-outline-teal w-100">
                          Get started
                        </Button>
                      )}
                    </div>
                  </div>
                </Col>
              );
            })}
          </Row>
        </Container>
      </section>
    </>
  );
}

export default PlanDetails;
